import { useMemo } from 'react';
import styled from 'styled-components';

import { Dimensions, Metrics } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import Tooltip from 'components/shared/Tooltip';
import { HOME_HEALTH_AGENCY } from 'constants/locationTypes';
import { useReferralsInsightsStore } from 'stores/referralsInsightsStore';
import { colors } from 'styles/theme/colors';

import MetricDetailLayout from './MetricDetailLayout';
import { buildBreakdownRows, readReferralRate } from './rollupHelpers';
import { RateMetricTiles } from './statTiles';
import useSlideoutAnalytics, { useSlideoutDimensionCounts } from './useSlideoutAnalytics';

type Props = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
};

// Service refusals are an HHA-only concept.
type RefusalType = 'rehab_facility' | 'patient';

const REFUSAL_LABELS: Record<RefusalType, string> = {
  rehab_facility: 'Refused by Home Health',
  patient: 'Refused by Patient',
};

const REFUSAL_COLORS: Record<RefusalType, string> = {
  rehab_facility: colors.primaryBlue,
  patient: colors.primaryBlue50,
};

const REFUSAL_ORDER: RefusalType[] = ['rehab_facility', 'patient'];

export default function AcceptanceRateDetailHeader({ loading, rollups }: Props) {
  const selectedReceivingGroupType = useReferralsInsightsStore((state) => state.selectedReceivingGroupType);
  const isHHA = selectedReceivingGroupType?.apiName === HOME_HEALTH_AGENCY;

  const { topAcceptingReceivingGroup, topAcceptingPlan, topReceivingLoading, topPlanLoading, partnerLabel } =
    useSlideoutAnalytics();
  const { rate, count, total } = readReferralRate(rollups, Metrics.ACCEPTANCE_RATE);

  const {
    names: refusalNames,
    counts: refusalCounts,
    loading: refusalLoading,
  } = useSlideoutDimensionCounts(Dimensions.SERVICE_REFUSAL_REFUSED_BY_TYPE, 'referral-slideout-service-refusals', {
    enabled: isHHA,
  });

  const refusals = useMemo(() => {
    const rows = buildBreakdownRows(refusalNames, refusalCounts, { lowercase: true }).sort(
      (a, b) => REFUSAL_ORDER.indexOf(a.name as RefusalType) - REFUSAL_ORDER.indexOf(b.name as RefusalType)
    );
    return { rows, total: rows.reduce((sum, r) => sum + r.count, 0) };
  }, [refusalNames, refusalCounts]);

  return (
    <MetricDetailLayout
      tiles={
        <RateMetricTiles
          rateLabel='Avg. Acceptance Rate'
          rateTone='green'
          rate={rate}
          count={count}
          total={total}
          ratePhrase='acceptance rate'
          loading={loading}
          topReceivingLoading={topReceivingLoading}
          topPlanLoading={topPlanLoading}
          topReceivingLabel={`Top Accepting ${partnerLabel}`}
          topReceiving={count > 0 ? topAcceptingReceivingGroup : null}
          topPlanLabel='Top Accepting Plan'
          topPlan={count > 0 ? topAcceptingPlan : null}
        />
      }
      chartTitle='Service Refusals for Accepted Referrals'
      chartLoading={refusalLoading}
      showChart={isHHA}
      isEmpty={refusals.total === 0}
      emptyMessage='No service refusals for accepted referrals to show for this time period.'>
      <BigNumber>{refusals.total.toLocaleString()}</BigNumber>
      <RefusalBars>
        {refusals.rows.map((r, i) => (
          <Tooltip
            key={r.name}
            text={`${REFUSAL_LABELS[r.name as RefusalType] ?? r.name}: ${r.count.toLocaleString()}`}>
            <RefusalBar
              style={{
                flex: r.count,
                background: REFUSAL_COLORS[r.name as RefusalType] ?? colors.black50,
                marginLeft: i === 0 ? 0 : 4,
              }}
            />
          </Tooltip>
        ))}
      </RefusalBars>
      <Legend>
        {refusals.rows.map((r) => {
          const pct = refusals.total > 0 ? (r.count / refusals.total) * 100 : 0;
          return (
            <LegendItem key={r.name}>
              <Swatch style={{ background: REFUSAL_COLORS[r.name as RefusalType] ?? colors.black50 }} />
              <LegendLabel>{REFUSAL_LABELS[r.name as RefusalType] ?? r.name}</LegendLabel>
              <LegendCount>{r.count.toLocaleString()}</LegendCount>
              <LegendPct>({pct.toFixed(1)}%)</LegendPct>
            </LegendItem>
          );
        })}
      </Legend>
    </MetricDetailLayout>
  );
}

const BigNumber = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 20px;
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  padding: 16px 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.black10};
  }
`;

const RefusalBars = styled.div`
  display: flex;
  width: 100%;
  height: 24px;
`;

const RefusalBar = styled.div`
  height: 100%;
  border-radius: 3px;
  min-width: 0;
`;

const Swatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const LegendLabel = styled.span`
  color: ${({ theme }) => theme.colors.black};
  flex: 1;
`;

const LegendCount = styled.span`
  color: ${({ theme }) => theme.colors.black};
  font-weight: 600;
`;

const LegendPct = styled.span`
  color: ${({ theme }) => theme.colors.black50};
`;
